import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { usePricingNav } from "../../hooks/usePricingNav";
import {
  Box,
  Typography,
  Container,
  Button,
  Grid,
  Avatar,
  Stack,
  Skeleton,
  Alert,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import {
  ArrowBack,
  CheckCircle,
  FavoriteBorder,
  Share,
  LockOutlined,
  Fullscreen,
  FullscreenExit,
} from "@mui/icons-material";
import {
  MEMBERSHIP_COLORS,
  ContentContainer,
} from "../../components/membership/MembershipStyles";
import videosService, { Video } from "../../services/videosService";
import { VideoPlayer } from "../../components/common/VideoPlayer";
import { NO_PLAYBACK, VideoPlayback, resolveVideoPlayback } from "../../utils/videoPlayback";

const VideoDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const goToPricing = usePricingNav();

  const [video, setVideo] = useState<Video | null>(null);
  const [playback, setPlayback] = useState<VideoPlayback>(NO_PLAYBACK);
  const [thumbnailUrl, setThumbnailUrl] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deniedIsCompanion, setDeniedIsCompanion] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery("(max-width:900px)");

  useEffect(() => {
    const fetchVideo = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        setError(null);
        const response = await videosService.getVideo(id);
        setVideo(response.video);
        // The API says which player this video needs; the response-level fields
        // win over the nested ones when both are present.
        setPlayback(
          resolveVideoPlayback({
            playbackKind: response.playbackKind ?? response.video.playbackKind,
            iframeUrl: response.iframeUrl || response.video.iframeUrl,
            playbackUrl: response.playbackUrl || response.video.playbackUrl,
            cloudflareVideoId: response.video.cloudflareVideoId,
          })
        );
        setThumbnailUrl(response.thumbnailUrl);
      } catch (err: any) {
        if (err.response?.status === 403) {
          setAccessDenied(true);
          // A leaflet's companion video is a per-leaflet add-on, so telling the
          // member to upgrade their plan would send them somewhere that does
          // not unlock it.
          setDeniedIsCompanion(err.response?.data?.code === "COMPANION_VIDEO_NOT_PURCHASED");
        } else {
          setError(err.response?.data?.error || "Failed to load video");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchVideo();
  }, [id]);

  const formatDuration = (seconds?: number) => {
    if (!seconds) return "--:--";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleFullscreen = async () => {
    const container = videoContainerRef.current;
    if (!container) return;
    try {
      if (!document.fullscreenElement && !(document as any).webkitFullscreenElement) {
        if (container.requestFullscreen) {
          await container.requestFullscreen();
        } else if ((container as any).webkitRequestFullscreen) {
          (container as any).webkitRequestFullscreen();
        }
        try {
          if (window.screen.orientation && typeof (window.screen.orientation as any).lock === "function") {
            await (window.screen.orientation as any).lock("landscape");
          }
        } catch {
          // Orientation lock not supported on this device
        }
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          (document as any).webkitExitFullscreen();
        }
      }
    } catch {
      // Fullscreen not supported
    }
  };

  useEffect(() => {
    const onFullscreenChange = () => {
      const isFull = !!(document.fullscreenElement || (document as any).webkitFullscreenElement);
      setIsFullscreen(isFull);
      if (!isFull) {
        try {
          if (window.screen.orientation && typeof (window.screen.orientation as any).unlock === "function") {
            (window.screen.orientation as any).unlock();
          }
        } catch {
          // Ignore
        }
      }
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    document.addEventListener("webkitfullscreenchange", onFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", onFullscreenChange);
    };
  }, []);

  // Loading state
  if (loading) {
    return (
      <Box sx={{ backgroundColor: MEMBERSHIP_COLORS.bg, minHeight: "100vh" }}>
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            borderBottom: "1px solid rgba(0,0,0,0.05)",
            py: 2,
          }}
        >
          <Container maxWidth="xl">
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate(-1)}
              sx={{
                color: MEMBERSHIP_COLORS.text,
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              Back
            </Button>
          </Container>
        </Box>
        <ContentContainer maxWidth="xl">
          <Grid container spacing={6}>
            <Grid size={{ xs: 12, lg: 8 }}>
              <Skeleton variant="rectangular" height={450} sx={{ borderRadius: 4 }} />
              <Skeleton variant="text" width="60%" height={48} sx={{ mt: 3 }} />
              <Skeleton variant="text" width="90%" />
              <Skeleton variant="text" width="85%" />
            </Grid>
            <Grid size={{ xs: 12, lg: 4 }}>
              <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 4 }} />
            </Grid>
          </Grid>
        </ContentContainer>
      </Box>
    );
  }

  // Access denied — show upgrade CTA
  if (accessDenied) {
    return (
      <Box
        sx={{
          backgroundColor: MEMBERSHIP_COLORS.bg,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            maxWidth: 480,
            px: 4,
            py: 6,
            borderRadius: "24px",
            backgroundColor: "#fff",
            boxShadow: "0 20px 60px rgba(0,0,0,0.10)",
            border: "1px solid rgba(0,0,0,0.06)",
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              backgroundColor: "rgba(216,154,122,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 3,
            }}
          >
            <LockOutlined sx={{ fontSize: 36, color: MEMBERSHIP_COLORS.accent }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, color: MEMBERSHIP_COLORS.text }}>
            {deniedIsCompanion ? "Video Add-On Required" : "Upgrade Your Plan"}
          </Typography>
          <Typography variant="body1" sx={{ color: MEMBERSHIP_COLORS.secondary, mb: 4, lineHeight: 1.7 }}>
            {deniedIsCompanion
              ? "This video comes with its leaflet as a paid add-on, and it was not included in your order. Add it to your plan to watch it."
              : "This video is not included in your current plan. Upgrade to access this content and unlock the full Dr.Paw library."}
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "12px",
                borderColor: MEMBERSHIP_COLORS.accent,
                color: MEMBERSHIP_COLORS.accent,
                px: 3,
              }}
            >
              Go Back
            </Button>
            <Button
              variant="contained"
              onClick={() => goToPricing()}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "12px",
                backgroundColor: MEMBERSHIP_COLORS.accent,
                color: "#fff",
                px: 3,
                "&:hover": { backgroundColor: "#C4836A" },
              }}
            >
              View Plans
            </Button>
          </Stack>
        </Box>
      </Box>
    );
  }

  // Error or not found state
  if (error || !video) {
    return (
      <Box
        sx={{
          backgroundColor: MEMBERSHIP_COLORS.bg,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          {error || "Video Not Found"}
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate("/membership/classes")}
          sx={{
            backgroundColor: MEMBERSHIP_COLORS.text,
            borderRadius: "12px",
            px: 4,
          }}
        >
          Back to Classes
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: MEMBERSHIP_COLORS.bg, minHeight: "100vh" }}>
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          borderBottom: "1px solid rgba(0,0,0,0.05)",
          py: 2,
        }}
      >
        <Container maxWidth="xl">
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{
              color: MEMBERSHIP_COLORS.text,
              fontWeight: 700,
              textTransform: "none",
            }}
          >
            Back to Classes
          </Button>
        </Container>
      </Box>

      <ContentContainer maxWidth="xl">
        <Grid container spacing={6}>
          {/* Main Video Section */}
          <Grid size={{ xs: 12, lg: 8 }}>
            <Box
              ref={videoContainerRef}
              sx={{
                width: "100%",
                aspectRatio: "16/9",
                backgroundColor: "#3D3630",
                borderRadius: { xs: "16px", md: "24px" },
                overflow: "hidden",
                boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
                position: "relative",
                "&:fullscreen": { borderRadius: 0, aspectRatio: "unset", height: "100vh" },
                "&:-webkit-full-screen": { borderRadius: 0, aspectRatio: "unset", height: "100vh" },
              }}
            >
              {playback.kind !== "none" ? (
                <VideoPlayer
                  playback={playback}
                  title={video.title}
                  poster={thumbnailUrl || video.thumbnailUrl}
                />
              ) : (
                <Box
                  component="img"
                  src={thumbnailUrl || "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&w=800&q=80"}
                  alt={video.title}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              )}
            </Box>

            {/* Mobile fullscreen button */}
            {isMobile && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                <IconButton
                  onClick={handleFullscreen}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(0,0,0,0.06)",
                    borderRadius: "8px",
                    px: 1.5,
                    gap: 0.5,
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    color: MEMBERSHIP_COLORS.secondary,
                  }}
                >
                  {isFullscreen ? (
                    <><FullscreenExit sx={{ fontSize: 18 }} /> Exit Fullscreen</>
                  ) : (
                    <><Fullscreen sx={{ fontSize: 18 }} /> Fullscreen</>
                  )}
                </IconButton>
              </Box>
            )}

            <Box sx={{ mt: { xs: 3, md: 5 } }}>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                {video.visibility === "members" && (
                  <Box
                    sx={{
                      backgroundColor: "rgba(216, 154, 122, 0.1)",
                      color: MEMBERSHIP_COLORS.accent,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: "4px",
                      fontWeight: 700,
                      fontSize: "0.7rem",
                      letterSpacing: "0.05em",
                    }}
                  >
                    MEMBERS
                  </Box>
                )}
                {video.tags.slice(0, 3).map((tag) => (
                  <Typography
                    key={tag}
                    variant="overline"
                    sx={{
                      color: MEMBERSHIP_COLORS.accent,
                      fontWeight: 800,
                      letterSpacing: "0.1em",
                    }}
                  >
                    {tag}
                  </Typography>
                ))}
              </Stack>

              <Typography
                variant="h3"
                sx={{ fontWeight: 800, mb: 2, color: MEMBERSHIP_COLORS.text, fontSize: { xs: "1.75rem", sm: "2.2rem", md: "3rem" } }}
              >
                {video.title}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: MEMBERSHIP_COLORS.secondary,
                  lineHeight: 1.8,
                  fontSize: "1.1rem",
                  mb: 4,
                }}
              >
                {video.description || "No description available for this video."}
              </Typography>

              {video.status !== "ready" && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  This video is currently {video.status}. Please check back shortly.
                </Alert>
              )}

              <Box
                sx={{
                  p: 3,
                  backgroundColor: "#FFFFFF",
                  borderRadius: "20px",
                  border: "1px solid rgba(0,0,0,0.05)",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Video Details
                </Typography>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <CheckCircle sx={{ color: MEMBERSHIP_COLORS.accent, fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Duration: {formatDuration(video.duration)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <CheckCircle sx={{ color: MEMBERSHIP_COLORS.accent, fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Access: {video.visibility === "members" ? "Members Only" : "Public"}
                    </Typography>
                  </Stack>
                  {video.tags.length > 0 && (
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <CheckCircle sx={{ color: MEMBERSHIP_COLORS.accent, fontSize: 20 }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Tags: {video.tags.join(", ")}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Box>
            </Box>
          </Grid>

          {/* Sidebar Section */}
          <Grid size={{ xs: 12, lg: 4 }}>
            <Box
              sx={{
                p: { xs: 3, md: 4 },
                backgroundColor: "#FFFFFF",
                borderRadius: "24px",
                position: { lg: "sticky" },
                top: { lg: 100 },
              }}
            >
              {video.createdBy && (
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ mb: 4, alignItems: "center" }}
                >
                  <Avatar
                    sx={{
                      width: 60,
                      height: 60,
                      bgcolor: MEMBERSHIP_COLORS.accent,
                    }}
                  >
                    {video.createdBy.name?.charAt(0) || "A"}
                  </Avatar>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: MEMBERSHIP_COLORS.secondary }}
                    >
                      Created by
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {video.createdBy.name || "Admin"}
                    </Typography>
                  </Box>
                </Stack>
              )}

              <Stack spacing={2}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<FavoriteBorder />}
                  sx={{
                    py: 1.5,
                    borderRadius: "12px",
                    backgroundColor: MEMBERSHIP_COLORS.text,
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  Save to Favorites
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Share />}
                  sx={{
                    py: 1.5,
                    borderRadius: "12px",
                    borderColor: "rgba(0,0,0,0.1)",
                    color: MEMBERSHIP_COLORS.text,
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  Share Video
                </Button>
              </Stack>

              <Box sx={{ mt: 5 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                  More Videos
                </Typography>
                <Button
                  fullWidth
                  variant="text"
                  onClick={() => navigate("/membership/classes")}
                  sx={{
                    color: MEMBERSHIP_COLORS.accent,
                    fontWeight: 600,
                    textTransform: "none",
                  }}
                >
                  Browse All Classes →
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </ContentContainer>
    </Box>
  );
};

export default VideoDetail;
